// Game constants
export const ROWS = 6;
export const COLS = 7;

export const Player = {
  NONE: 0,
  RED: 1,
  YELLOW: 2
};

// Reset the game board
export const createEmptyBoard = () => {
  return Array(ROWS).fill().map(() => Array(COLS).fill(Player.NONE));
};

// Check if a player has won
export const checkWin = (board, player) => {
  // Check all positions for a potential win
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      if (board[row][col] === player) {
        // Check horizontal (right)
        if (col + 3 < COLS &&
            board[row][col + 1] === player &&
            board[row][col + 2] === player &&
            board[row][col + 3] === player) {
          return true;
        }
        
        // Check vertical (down)
        if (row + 3 < ROWS &&
            board[row + 1][col] === player &&
            board[row + 2][col] === player &&
            board[row + 3][col] === player) {
          return true;
        }
        
        // Check diagonal (down-right)
        if (row + 3 < ROWS && col + 3 < COLS &&
            board[row + 1][col + 1] === player &&
            board[row + 2][col + 2] === player &&
            board[row + 3][col + 3] === player) {
          return true;
        }
        
        // Check diagonal (down-left)
        if (row + 3 < ROWS && col - 3 >= 0 &&
            board[row + 1][col - 1] === player &&
            board[row + 2][col - 2] === player &&
            board[row + 3][col - 3] === player) {
          return true;
        }
      }
    }
  }
  return false;
};

// Check if the board is full (draw)
export const isBoardFull = (board) => {
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      if (board[row][col] === Player.NONE) {
        return false;
      }
    }
  }
  return true;
};

// Check if a column is valid for a move
export const isValidMove = (board, col) => {
  return col >= 0 && col < COLS && board[0][col] === Player.NONE;
};

// Make a move and return the row where the piece was placed
export const makeMove = (board, col, player) => {
  if (!isValidMove(board, col)) return -1;
  
  for (let row = ROWS - 1; row >= 0; row--) {
    if (board[row][col] === Player.NONE) {
      board[row][col] = player;
      return row;
    }
  }
  return -1;
};

// Undo a move
export const undoMove = (board, col) => {
  for (let row = 0; row < ROWS; row++) {
    if (board[row][col] !== Player.NONE) {
      board[row][col] = Player.NONE;
      return row;
    }
  }
  return -1;
};

// Evaluate a position for a specific player (used by AI)
export const evaluatePosition = (board, player) => {
  let score = 0;
  
  // Check all possible 4-in-a-row combinations
  for (let row = 0; row < ROWS; row++) {
    for (let col = 0; col < COLS; col++) {
      // Horizontal
      if (col + 3 < COLS) {
        score += evaluateWindow(board, row, col, 0, 1, player);
      }
      // Vertical
      if (row + 3 < ROWS) {
        score += evaluateWindow(board, row, col, 1, 0, player);
      }
      // Diagonal (down-right)
      if (row + 3 < ROWS && col + 3 < COLS) {
        score += evaluateWindow(board, row, col, 1, 1, player);
      }
      // Diagonal (down-left)
      if (row + 3 < ROWS && col - 3 >= 0) {
        score += evaluateWindow(board, row, col, 1, -1, player);
      }
    }
  }
  
  return score;
};

// Evaluate a window of 4 cells
const evaluateWindow = (board, row, col, deltaRow, deltaCol, player) => {
  let playerCount = 0;
  let opponentCount = 0;
  let emptyCount = 0;
  
  const opponent = player === Player.RED ? Player.YELLOW : Player.RED;
  
  for (let i = 0; i < 4; i++) {
    const currentRow = row + i * deltaRow;
    const currentCol = col + i * deltaCol;
    const cell = board[currentRow][currentCol];
    
    if (cell === player) {
      playerCount++;
    } else if (cell === opponent) {
      opponentCount++;
    } else {
      emptyCount++;
    }
  }
  
  // If opponent has pieces in this window, it's not useful for us
  if (opponentCount > 0 && playerCount > 0) {
    return 0;
  }
  
  // Score based on how many pieces we have
  if (playerCount === 4) return 1000;
  if (playerCount === 3 && emptyCount === 1) return 100;
  if (playerCount === 2 && emptyCount === 2) return 10;
  if (playerCount === 1 && emptyCount === 3) return 1;
  
  // Penalize opponent's good positions
  if (opponentCount === 4) return -1000;
  if (opponentCount === 3 && emptyCount === 1) return -100;
  if (opponentCount === 2 && emptyCount === 2) return -10;
  if (opponentCount === 1 && emptyCount === 3) return -1;
  
  return 0;
};

// Get all valid moves
export const getValidMoves = (board) => {
  const moves = [];
  for (let col = 0; col < COLS; col++) {
    if (isValidMove(board, col)) {
      moves.push(col);
    }
  }
  return moves;
};

// Minimax algorithm with alpha-beta pruning for AI (optimized)
export const minimax = (board, depth, alpha, beta, maximizingPlayer, aiPlayer) => {
  const opponent = aiPlayer === Player.RED ? Player.YELLOW : Player.RED;
  
  // Terminal states
  if (checkWin(board, aiPlayer)) {
    return { score: 2000 + depth, move: -1 };
  }
  if (checkWin(board, opponent)) {
    return { score: -2000 - depth, move: -1 };
  }
  if (depth === 0 || isBoardFull(board)) {
    return { score: evaluatePosition(board, aiPlayer), move: -1 };
  }
  
  const validMoves = getValidMoves(board);
  
  // Move ordering: prioritize center columns for better pruning
  validMoves.sort((a, b) => Math.abs(3 - a) - Math.abs(3 - b));
  
  let bestMove = validMoves[0];
  
  if (maximizingPlayer) {
    let maxEval = -Infinity;
    
    for (const col of validMoves) {
      const row = makeMove(board, col, aiPlayer);
      if (row !== -1) {
        const eval_result = minimax(board, depth - 1, alpha, beta, false, aiPlayer);
        undoMove(board, col);
        
        if (eval_result.score > maxEval) {
          maxEval = eval_result.score;
          bestMove = col;
        }
        
        alpha = Math.max(alpha, eval_result.score);
        if (beta <= alpha) {
          break; // Alpha-beta pruning
        }
      }
    }
    
    return { score: maxEval, move: bestMove };
  } else {
    let minEval = Infinity;
    
    for (const col of validMoves) {
      const row = makeMove(board, col, opponent);
      if (row !== -1) {
        const eval_result = minimax(board, depth - 1, alpha, beta, true, aiPlayer);
        undoMove(board, col);
        
        if (eval_result.score < minEval) {
          minEval = eval_result.score;
          bestMove = col;
        }
        
        beta = Math.min(beta, eval_result.score);
        if (beta <= alpha) {
          break; // Alpha-beta pruning
        }
      }
    }
    
    return { score: minEval, move: bestMove };
  }
};

// Get the best move for the AI with optimized performance
export const getBestMove = (board, difficulty = 6) => {
  const boardCopy = board.map(row => [...row]);
  
  // Use iterative deepening with time limit for fast response
  const startTime = Date.now();
  const timeLimit = 50; // 50ms maximum thinking time for instant response
  let bestMove = 3; // Default center column
  let depth = 1;
  
  // Quick win/block check first (instant)
  const quickMove = getEnhancedQuickMove(boardCopy);
  if (quickMove !== -1) {
    return quickMove;
  }
  
  // Iterative deepening search with strict time limit
  while (depth <= Math.min(difficulty, 4) && (Date.now() - startTime) < timeLimit) {
    try {
      const result = minimax(boardCopy, depth, -Infinity, Infinity, true, Player.YELLOW);
      if (result.move !== -1) {
        bestMove = result.move;
      }
      depth++;
    } catch (e) {
      break; // Time limit exceeded
    }
  }
  
  return bestMove;
};

// Additional AI optimization: Pre-computed move priorities
const MOVE_PRIORITIES = [3, 2, 4, 1, 5, 0, 6]; // Center-first ordering

// Enhanced quick move detection with tactical awareness
const getEnhancedQuickMove = (board) => {
  const validMoves = getValidMoves(board);
  
  // Priority 1: Win immediately
  for (const col of validMoves) {
    const row = makeMove(board, col, Player.YELLOW);
    if (row !== -1) {
      if (checkWin(board, Player.YELLOW)) {
        undoMove(board, col);
        return col;
      }
      undoMove(board, col);
    }
  }
  
  // Priority 2: Block opponent's immediate win
  for (const col of validMoves) {
    const row = makeMove(board, col, Player.RED);
    if (row !== -1) {
      if (checkWin(board, Player.RED)) {
        undoMove(board, col);
        return col;
      }
      undoMove(board, col);
    }
  }
  
  // Priority 3: Look for setup moves (creating multiple threats)
  for (const col of MOVE_PRIORITIES) {
    if (validMoves.includes(col)) {
      const row = makeMove(board, col, Player.YELLOW);
      if (row !== -1) {
        const score = evaluatePosition(board, Player.YELLOW);
        undoMove(board, col);
        if (score > 50) {
          return col;
        }
      }
    }
  }
  
  // Default: return center column
  return validMoves.includes(3) ? 3 : validMoves[0];
};
